import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { formatDate, formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button, Icon, ImageSlot } from '../../../ds/components';
import { rentBasisHint } from '../rent';
import type { CalculatorContext, InvestmentInputValues } from '../types';
import { eur, eur2, pct, sgn, type CalculatorView, type YearBar } from './model';
import { SliderField } from './SliderField';
import styles from './calculator.module.css';

function Question({
  number,
  title,
  text,
  children,
  aside,
}: {
  number: string;
  title: string;
  text?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={styles.question} aria-label={title}>
      <div>
        <div className={styles.questionNumber}>Frage {number}</div>
        <h2 className={styles.questionTitle}>{title}</h2>
        {text ? <p className={styles.questionText}>{text}</p> : null}
        {aside}
      </div>
      <div className={styles.questionBody}>{children}</div>
    </section>
  );
}

function Legend({
  items,
  className,
}: {
  items: { swatch: string | undefined; label: string }[];
  className?: string | undefined;
}) {
  return (
    <div className={clsx(styles.legend, className)}>
      {items.map((item) => (
        <span key={item.label} className={styles.legendItem}>
          <span className={clsx(styles.swatch, item.swatch)} aria-hidden="true" />
          {item.label}
        </span>
      ))}
    </div>
  );
}

function pctOf(fraction: number): string {
  return `${Math.max(0, Math.min(100, fraction * 100)).toFixed(1)}%`;
}

interface BarsProps {
  bars: YearBar[];
  onSelect: (year: number) => void;
  ink?: boolean | undefined;
}

/** The 30-year stacked bars of Frage 04 and the summary: repayment by payer plus appreciation, losses downward. */
function YearBars({ bars, onSelect, ink = false }: BarsProps) {
  return (
    <>
      <div
        className={clsx(styles.bars, ink && styles.barsTall)}
        role="group"
        aria-label="Gewinn je Verkaufsjahr"
      >
        <span className={clsx(styles.barsZero, ink && styles.barsZeroInk)} aria-hidden="true" />
        {bars.map((bar) => (
          <button
            key={bar.year}
            type="button"
            className={clsx(styles.bar, bar.selected && styles.barSelected)}
            aria-label={`Verkauf in Jahr ${bar.year}`}
            aria-pressed={bar.selected}
            onClick={() => onSelect(bar.year)}
          >
            <span className={styles.barUp}>
              <span
                className={ink ? styles.segInkAppreciation : styles.segAppreciation}
                style={{ height: pctOf(bar.appreciation), borderRadius: '3px 3px 0 0' }}
              />
              <span className={ink ? styles.segInkOwn : styles.segOwn} style={{ height: pctOf(bar.own) }} />
              <span
                className={ink ? styles.segInkTax : styles.segTax}
                style={{ height: pctOf(bar.taxOffice) }}
              />
              <span
                className={ink ? styles.segInkTenant : styles.segTenant}
                style={{ height: pctOf(bar.tenant) }}
              />
            </span>
            <span className={styles.barDown}>
              <span
                className={ink ? styles.segInkLoss : styles.segLoss}
                style={{ height: pctOf(bar.loss), borderRadius: '0 0 3px 3px' }}
              />
            </span>
          </button>
        ))}
      </div>
      <div className={styles.barLabels} aria-hidden="true">
        {bars.map((bar) => (
          <span
            key={bar.year}
            className={clsx(
              styles.barLabel,
              ink && styles.inkLabel,
              bar.year % 5 !== 0 && styles.barLabelHidden,
            )}
          >
            {bar.year}
          </span>
        ))}
      </div>
    </>
  );
}

export interface ResultsTabProps {
  context: CalculatorContext;
  values: InvestmentInputValues;
  view: CalculatorView;
  onYearChange: (year: number) => void;
  onInstallmentYearChange: (year: number) => void;
  onSendPdf: () => void;
}

/** „Kundenansicht" (IR-02): the nine questions and the dark summary, on the same inputs as the advisor view. */
export function ResultsTab({
  context,
  values,
  view,
  onYearChange,
  onInstallmentYearChange,
  onSendPdf,
}: ResultsTabProps) {
  const { unit } = context;
  const { result, split, saleYearRow: sale, tax, runningCosts, reserve } = view;
  const saleInfo = result.sale;
  const year = values.saleYear;
  const installmentRow = result.years[values.installmentYear - 1] ?? result.years[0];
  const locationLine = unit.address ?? unit.propertyName;
  const objMeta = `${locationLine} · ${formatInteger(Math.round(values.areaSqm))} m² · ${unit.number}`;
  const equityShare = sale.propertyValueCents > 0 ? sale.equityCents / sale.propertyValueCents : 0;
  const appreciationShare = sale.equityCents > 0 ? Math.max(0, sale.appreciationCents) / sale.equityCents : 0;
  const repaidShare = sale.equityCents > 0 ? sale.repaidCents / sale.equityCents : 0;
  const repaidTotal = sale.repaidCents || 1;
  const legendMain = [
    { swatch: styles.segAppreciation, label: 'Wertzuwachs' },
    { swatch: styles.segTenant, label: 'Tilgung, vom Mieter' },
    { swatch: styles.segTax, label: 'Tilgung, vom Finanzamt' },
    { swatch: styles.segOwn, label: 'Tilgung, aus eigener Tasche' },
  ];
  const legendInk = [
    { swatch: styles.segInkAppreciation, label: 'Wertzuwachs' },
    { swatch: styles.segInkTenant, label: 'Tilgung, vom Mieter' },
    { swatch: styles.segInkTax, label: 'Tilgung, vom Finanzamt' },
    { swatch: styles.segInkOwn, label: 'Tilgung, aus eigener Tasche' },
  ];
  const splitTotal = split.totalCents || 1;
  const rentHint = rentBasisHint(values.rentCents, unit.rentActualCents, unit.rentSubsidyCents);
  const percentOfSplit = (cents: number) => `${formatInteger(Math.round((cents / splitTotal) * 100))} %`;

  return (
    <>
      <div className={styles.lead}>
        <div className={styles.leadText}>
          <div className={styles.eyebrow11}>{objMeta}</div>
          <p className={styles.leadSentence}>
            Sie bringen <span className={clsx(view.empty && styles.blur)}>{eur(result.equityCents)}</span>{' '}
            Eigenkapital ein und zahlen{' '}
            <span className={clsx(view.empty && styles.blur)}>{eur(split.ownCents)}</span> im Monat. Bei
            Verkauf in Jahr {year} bleibt ein Gewinn von{' '}
            <span className={clsx(view.empty && styles.blur)}>{eur(sale.equityCents)}</span>.
          </p>
          {view.empty ? (
            <div className={styles.leadEmpty}>
              <Icon icon={Info} size={16} />
              <span>
                Noch keine Angaben erfasst. Sobald Objekt und Finanzierung stehen, erscheinen die Zahlen.
              </span>
            </div>
          ) : null}
        </div>
        <div className={styles.leadActions}>
          <Button size="small" onClick={onSendPdf}>
            Als PDF für den Mandanten
          </Button>
        </div>
      </div>

      <Question
        number="01"
        title="Wo ist das Objekt? Wie gut ist es?"
        text="Bevor über Zahlen gesprochen wird: die Wohnung selbst."
      >
        <div className={styles.photos}>
          <ImageSlot
            chrome="frame"
            alt="Hauptbild"
            placeholder="Hauptbild · Wohnzimmer oder Fassade"
            height={240}
            radius="media"
          />
          <ImageSlot chrome="frame" alt="Küche" placeholder="Küche" height={240} radius="media" />
          <div className={styles.photoStack}>
            <ImageSlot chrome="frame" alt="Bad" placeholder="Bad" ratio="4 / 3" radius="media" />
            <ImageSlot
              chrome="frame"
              alt="Grundriss"
              placeholder="Grundriss"
              kind="plan"
              ratio="4 / 3"
              radius="media"
            />
          </div>
        </div>
        <h3 className={styles.objectTitle}>{unit.propertyName}</h3>
        <div className={styles.objectSub}>
          {[locationLine, unit.number, unit.floor].filter(Boolean).join(' · ')}
        </div>
        <div className={clsx(styles.card, styles.compRow)}>
          <div className={styles.eyebrow11}>Das Objekt</div>
          <div className={styles.factRows}>
            <div className={styles.factRow}>
              <span className={styles.factLabel}>Baujahr</span>
              <span className={styles.factValue}>{unit.yearBuilt ?? '—'}</span>
            </div>
            <div className={styles.factRow}>
              <span className={styles.factLabel}>Ausstattung</span>
              <span className={styles.factValue}>{unit.fittings ?? '—'}</span>
            </div>
            <div className={styles.factRow}>
              <span className={styles.factLabel}>Energieausweis</span>
              <span className={styles.factValue}>{unit.energyCertificate ?? '—'}</span>
            </div>
            <div className={styles.factRow}>
              <span className={styles.factLabel}>Vermietung</span>
              <span className={styles.factValue}>
                {unit.rented
                  ? unit.rentedSinceOn
                    ? `vermietet seit ${formatDate(unit.rentedSinceOn)}`
                    : 'vermietet'
                  : 'nicht vermietet'}
              </span>
            </div>
            <div className={styles.factRow}>
              <span className={styles.factLabel}>Hausgeld, Rücklage</span>
              <span className={styles.factValue}>
                {unit.serviceChargeCents === null
                  ? '—'
                  : `${eur(unit.serviceChargeCents)} · davon ${eur(unit.reserveShareCents ?? 0)} Rücklage`}
              </span>
            </div>
          </div>
          <div className={styles.cardSunken} style={{ marginTop: 22, padding: '18px 20px' }}>
            <div className={styles.eyebrow11}>Einordnung in den Markt</div>
            <div className={styles.rowBetween} style={{ marginTop: 12 }}>
              <span className={styles.factLabel}>Kaufpreis je m²</span>
              <span className={styles.smallValue}>
                {values.areaSqm > 0 ? eur(values.purchasePriceCents / values.areaSqm) : '—'}
              </span>
            </div>
            <div className={styles.rowBetween} style={{ marginTop: 14 }}>
              <span className={styles.factLabel}>Miete je m²</span>
              <span className={styles.smallValue}>
                {values.areaSqm > 0 ? eur2(values.rentCents / values.areaSqm) : '—'}
              </span>
            </div>
            {rentHint ? <div className={styles.valueNoteSmall}>{rentHint}</div> : null}
          </div>
        </div>
      </Question>

      <Question
        number="02"
        title="Was zahle ich?"
        text={
          result.equityCents > 0
            ? 'Eigenkapital beim Kauf, der Rest läuft über die Bank.'
            : 'Kaufpreis und Nebenkosten laufen über die Bank. Es fließt kein Eigenkapital.'
        }
      >
        <div className={styles.twoCols}>
          <div className={clsx(styles.card, styles.cardLarge)}>
            <div className={styles.eyebrow11}>Einmal, beim Kauf</div>
            <div className={styles.bigValue}>{eur(result.equityCents)}</div>
            <div className={styles.valueNote}>
              Darlehen {eur(result.loanCents)} — Kaufpreis plus {pct(values.acquisitionCostsPct, 2)}{' '}
              Kaufnebenkosten ({eur(result.acquisitionCostsCents)}). Gesamtinvestition{' '}
              {eur(result.totalInvestmentCents)}.
              {values.sellerPaysAcquisitionCosts ? ' Die Kaufnebenkosten trägt der Verkäufer.' : ''}
            </div>
          </div>
          <div className={clsx(styles.card, styles.cardLarge)}>
            <div className={styles.eyebrow11}>Jeden Monat, aus Ihrer Tasche</div>
            <div className={styles.bigValue}>{eur(split.ownCents)}</div>
            <div className={styles.valueNote}>
              Das ist der Rest, nachdem Mieter und Finanzamt bezahlt haben.
            </div>
          </div>
        </div>
        <div className={styles.cardSunken} style={{ marginTop: 20 }}>
          <div className={styles.rowBetween}>
            <span className={styles.eyebrow11}>Wer zahlt die Rate · Jahr {values.installmentYear}</span>
            <span className={styles.valueNoteSmall} style={{ marginTop: 0, textAlign: 'right' }}>
              Zins {eur(split.interestCents)} · Tilgung {eur(split.principalCents)} · Kosten{' '}
              {eur(result.monthly.nonRecoverableCostsCents)} ={' '}
              <span className={styles.smallValue}>{eur(split.totalCents)}</span>
            </span>
          </div>
          <div
            className={styles.stackBar}
            role="img"
            aria-label={`Mieter ${percentOfSplit(split.tenantCents)}, Finanzamt ${percentOfSplit(split.taxOfficeCents)}, Sie ${percentOfSplit(split.ownCents)}`}
          >
            <span className={styles.segTenant} style={{ width: pctOf(split.tenantCents / splitTotal) }} />
            <span className={styles.segTax} style={{ width: pctOf(split.taxOfficeCents / splitTotal) }} />
            <span className={styles.segOwn} style={{ width: pctOf(split.ownCents / splitTotal) }} />
          </div>
          <Legend
            items={[
              { swatch: styles.segTenant, label: `Mieter  ${percentOfSplit(split.tenantCents)}` },
              { swatch: styles.segTax, label: `Finanzamt  ${percentOfSplit(split.taxOfficeCents)}` },
              { swatch: styles.segOwn, label: `Sie  ${percentOfSplit(split.ownCents)}` },
            ]}
          />
          <div className={styles.threeCols} style={{ marginTop: 20 }}>
            <div>
              <div className={styles.eyebrow11}>Der Mieter zahlt</div>
              <div className={styles.mediumValue}>{eur(split.tenantCents)}</div>
              <div className={styles.valueNoteSmall}>Die Soll-Miete aus der Eingabe.</div>
            </div>
            <div>
              <div className={clsx(styles.eyebrow11, styles.miniHeadAccent)}>Das Finanzamt zahlt</div>
              <div className={styles.mediumValue}>{eur(split.taxOfficeCents)}</div>
              <div className={styles.valueNoteSmall}>
                Über den Lohnsteuerermäßigungsantrag monatlich in Ihrer Lohnabrechnung — nicht erst mit dem
                Steuerbescheid.
              </div>
            </div>
            <div>
              <div className={styles.eyebrow11}>Sie zahlen</div>
              <div className={styles.mediumValue}>{eur(split.ownCents)}</div>
              <div className={styles.valueNoteSmall}>
                In der Rate stecken {eur(split.principalCents)} Tilgung — die zahlen Mieter, Finanzamt und Sie
                gemeinsam.
              </div>
            </div>
          </div>
          <div style={{ marginTop: 24 }}>
            <div className={styles.rowBetween}>
              <span className={styles.eyebrow11}>Aufteilung über 15 Jahre</span>
              <span className={styles.valueNoteSmall} style={{ marginTop: 0 }}>
                Die Miete steigt, der Zinsanteil sinkt — das Finanzamt trägt jedes Jahr weniger, der Mieter
                mehr.
              </span>
            </div>
            <div className={styles.strip} role="group" aria-label="Aufteilung je Jahr">
              {view.strip.map((bar) => (
                <button
                  key={bar.year}
                  type="button"
                  className={clsx(styles.stripBar, bar.selected && styles.stripBarSelected)}
                  aria-label={`Jahr ${bar.year} · Sie zahlen ${eur(bar.ownCents)}`}
                  aria-pressed={bar.selected}
                  onClick={() => onInstallmentYearChange(bar.year)}
                >
                  <span
                    className={styles.segOwn}
                    style={{ height: pctOf(bar.own), borderRadius: '4px 4px 0 0' }}
                  />
                  <span className={styles.segTax} style={{ height: pctOf(bar.taxOffice) }} />
                  <span
                    className={styles.segTenant}
                    style={{ height: pctOf(bar.tenant), borderRadius: '0 0 4px 4px' }}
                  />
                </button>
              ))}
            </div>
            <div className={styles.stripLabels} aria-hidden="true">
              {view.strip.map((bar) => (
                <span
                  key={bar.year}
                  className={clsx(styles.stripLabel, bar.selected && styles.stripLabelSelected)}
                >
                  {bar.year}
                </span>
              ))}
            </div>
            <div style={{ marginTop: 16 }}>
              <SliderField
                label="Jahr der Ratenaufteilung"
                min={1}
                max={15}
                step={1}
                value={values.installmentYear}
                display={`Jahr ${values.installmentYear}`}
                onChange={onInstallmentYearChange}
                small
              />
            </div>
          </div>
          <div className={styles.valueNoteSmall} style={{ marginTop: 22, fontSize: 13.5 }}>
            {view.taxOfficeExplanation}
          </div>
        </div>
      </Question>

      <Question
        number="03"
        title="Was bekomme ich?"
        aside={
          <div className={styles.yearPicker}>
            <SliderField
              label="Zeitpunkt"
              min={1}
              max={30}
              step={1}
              value={year}
              display={`Jahr ${year}`}
              onChange={onYearChange}
              small
            />
          </div>
        }
      >
        <div
          className={styles.rowBetween}
          style={{ alignItems: 'baseline', gap: 14, flexWrap: 'nowrap', justifyContent: 'flex-start' }}
        >
          <span className={styles.hugeValue}>{eur(sale.equityCents)}</span>
          <span className={styles.heroUnit} style={{ color: 'var(--text-muted)' }}>
            Gesamtgewinn bei Verkauf in Jahr {year}
          </span>
        </div>
        <div
          className={styles.stackBar}
          style={{ marginTop: 26 }}
          role="img"
          aria-label={`Ihr Gewinn ${formatInteger(Math.round(equityShare * 100))} % des Objektwerts`}
        >
          <span className={styles.segOwn} style={{ width: pctOf(equityShare) }} />
          <span className={styles.segDebt} style={{ width: pctOf(1 - equityShare) }} />
        </div>
        <Legend
          items={[
            {
              swatch: styles.segOwn,
              label: `Ihr Gewinn  ${formatInteger(Math.round(equityShare * 100))} % des Objektwerts`,
            },
            { swatch: styles.segDebt, label: `Restdarlehen  ${eur(sale.remainingDebtCents)}` },
          ]}
        />
        <div className={styles.fourCols} style={{ marginTop: 22 }}>
          <div>
            <div className={styles.eyebrow11}>Objektwert</div>
            <div className={styles.mediumValue}>{eur(sale.propertyValueCents)}</div>
          </div>
          <div>
            <div className={styles.eyebrow11}>Getilgt</div>
            <div className={styles.mediumValue}>{eur(sale.repaidCents)}</div>
          </div>
          <div>
            <div className={styles.eyebrow11}>Von Ihnen eingezahlt</div>
            <div className={styles.mediumValue}>{eur(sale.ownPaymentsCents)}</div>
          </div>
          <div>
            <div className={styles.eyebrow11}>Rendite pro Jahr</div>
            <div className={styles.mediumValue}>{pct(result.irrPct)}</div>
          </div>
        </div>
        <div className={styles.cardSunken} style={{ marginTop: 24, padding: '24px 26px' }}>
          <div className={styles.rowBetween}>
            <span className={styles.eyebrow11}>Verkauf in Jahr {year}</span>
            <span
              className={styles.valueNoteSmall}
              style={{ marginTop: 0, color: saleInfo.taxFree ? 'var(--text-accent)' : 'var(--danger-fg)' }}
            >
              {view.saleTaxLabel}
            </span>
          </div>
          <div className={styles.threeCols} style={{ marginTop: 18, gap: 24 }}>
            <div>
              <div className={styles.valueNoteSmall} style={{ marginTop: 0 }}>
                Verkaufserlös
              </div>
              <div className={styles.mediumValue} style={{ marginTop: 8 }}>
                {eur(saleInfo.proceedsCents)}
              </div>
            </div>
            <div>
              <div className={styles.valueNoteSmall} style={{ marginTop: 0 }}>
                abzüglich Restschuld
              </div>
              <div className={styles.mediumValue} style={{ marginTop: 8 }}>
                {eur(saleInfo.remainingDebtCents)}
              </div>
            </div>
            <div>
              <div className={styles.valueNoteSmall} style={{ marginTop: 0, color: 'var(--text-strong)' }}>
                Fließt Ihnen zu
              </div>
              <div className={styles.mediumValue} style={{ marginTop: 8 }}>
                {eur(saleInfo.netProceedsCents)}
              </div>
            </div>
          </div>
          <div className={styles.questionNote}>{view.saleNote}</div>
        </div>
      </Question>

      <Question
        number="04"
        title="Wie sieht der Zuwachs aus?"
        text="Jede Säule ist ein Jahr: der Gewinn, wenn Sie in diesem Jahr verkaufen. Gestapelt aus Wertzuwachs und Tilgung — und die Tilgung danach, wer sie getragen hat. Klicken Sie in eine Säule."
        aside={
          <>
            <div className={styles.questionAccent}>{view.aheadSentence}</div>
            <div style={{ marginTop: 22 }}>
              <div className={styles.eyebrow11}>Gesamtgewinn in Jahr {year}</div>
              <div className={styles.mediumValue} style={{ fontSize: 30 }}>
                {eur(sale.equityCents)}
              </div>
            </div>
          </>
        }
      >
        <YearBars bars={view.bars} onSelect={onYearChange} />
        <Legend className={styles.legendSmall} items={legendMain} />
      </Question>

      <Question number="05" title="Wie setzt er sich zusammen?" text={`Verkauf in Jahr ${year}.`}>
        <div className={styles.rowBetween}>
          <div>
            <div className={styles.eyebrow11}>Gesamtgewinn</div>
            <div className={styles.bigValue} style={{ marginTop: 12 }}>
              {eur(sale.equityCents)}
            </div>
          </div>
          <div className={styles.valueNote} style={{ marginTop: 0, textAlign: 'right' }}>
            Verkaufserlös {eur(saleInfo.proceedsCents)}
            <br />
            abzüglich Restdarlehen {eur(saleInfo.remainingDebtCents)}
          </div>
        </div>
        <div className={styles.compRow}>
          <div className={styles.rowBetween}>
            <span className={styles.compLabel}>Wertzuwachs</span>
            <span className={styles.mediumValue} style={{ marginTop: 0 }}>
              {eur(sale.appreciationCents)}
            </span>
          </div>
          <div
            className={clsx(styles.compBar, styles.segAppreciation)}
            style={{ width: pctOf(appreciationShare) }}
          />
          <div className={styles.valueNoteSmall}>
            Objektwert abzüglich Gesamtinvestition, {pct(values.appreciationPct)} pro Jahr angenommen
          </div>
        </div>
        <div className={styles.compRow}>
          <div className={styles.rowBetween}>
            <span className={styles.compLabel}>Tilgung des Darlehens</span>
            <span className={styles.mediumValue} style={{ marginTop: 0 }}>
              {eur(sale.repaidCents)}
            </span>
          </div>
          <div style={{ width: pctOf(repaidShare) }}>
            <div className={clsx(styles.stackBar, styles.stackBarThin)}>
              <span
                className={styles.segTenant}
                style={{ width: pctOf(sale.repaidByTenantCents / repaidTotal) }}
              />
              <span
                className={styles.segTax}
                style={{ width: pctOf(sale.repaidByTaxOfficeCents / repaidTotal) }}
              />
              <span
                className={styles.segOwn}
                style={{ width: pctOf(sale.repaidFromOwnPocketCents / repaidTotal) }}
              />
            </div>
          </div>
          <Legend
            className={styles.legendSmall}
            items={[
              { swatch: styles.segTenant, label: `vom Mieter ${eur(sale.repaidByTenantCents)}` },
              { swatch: styles.segTax, label: `vom Finanzamt ${eur(sale.repaidByTaxOfficeCents)}` },
              { swatch: styles.segOwn, label: `aus eigener Tasche ${eur(sale.repaidFromOwnPocketCents)}` },
            ]}
          />
        </div>
        <div className={styles.questionNote} style={{ marginTop: 18 }}>
          {view.disclaimer}
        </div>
      </Question>

      <Question
        number="06"
        title="Warum zahlt das Finanzamt mit?"
        text="Weil das Gesetz einen Aufwand anerkennt, der Ihr Konto nie verlässt: die Abschreibung auf das Gebäude."
        aside={<div className={styles.questionNote}>Erstes Jahr, Werte aus der Eingabe.</div>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div>
            <div className={styles.rowBetween}>
              <span className={styles.positionTitle} style={{ fontSize: 14 }}>
                Was der Mieter bringt · Kaltmiete (Ist)
              </span>
              <span className={styles.sliderValue}>{eur(tax.rentCents)}</span>
            </div>
            <div className={styles.tallBar}>
              <span className={styles.segTenant} style={{ width: pctOf(tax.widths.rent) }} />
            </div>
          </div>
          <div>
            <div className={styles.rowBetween}>
              <span className={styles.positionTitle} style={{ fontSize: 14 }}>
                Ausgaben, die Geld kosten
              </span>
              <span className={styles.sliderValue}>{eur(tax.cashCents)}</span>
            </div>
            <div className={styles.tallBar}>
              <span className={styles.segAppreciation} style={{ width: pctOf(tax.widths.interest) }} />
              <span style={{ width: pctOf(tax.widths.deductible), background: 'var(--sand-400)' }} />
            </div>
            <Legend
              className={styles.legendSmall}
              items={[
                { swatch: styles.segAppreciation, label: `Schuldzinsen  ${eur(tax.interestCents)}` },
                { swatch: styles.segOwn, label: `Bewirtschaftung  ${eur(tax.deductibleCents)}` },
              ]}
            />
            <div className={styles.valueNoteSmall} style={{ fontSize: 12.5 }}>
              Die Tilgung von {eur(tax.principalYearCents)} im Jahr geht zusätzlich vom Konto, zählt
              steuerlich aber nicht als Ausgabe — sie tauscht Schulden gegen Eigentum.
            </div>
          </div>
          <div>
            <div className={styles.rowBetween}>
              <span className={styles.positionTitle} style={{ fontSize: 14 }}>
                Was das Finanzamt als Aufwand anerkennt
              </span>
              <span className={styles.sliderValue}>{eur(tax.recognisedCents)}</span>
            </div>
            <div className={styles.tallBar}>
              <span className={styles.segAppreciation} style={{ width: pctOf(tax.widths.interest) }} />
              <span style={{ width: pctOf(tax.widths.deductible), background: 'var(--sand-400)' }} />
              <span className={styles.segStripe} style={{ width: pctOf(tax.widths.depreciation) }} />
            </div>
            <Legend
              className={styles.legendSmall}
              items={[
                {
                  swatch: styles.segStripe,
                  label: `AfA  ${eur(tax.depreciationCents)}  —  kostet kein Geld`,
                },
              ]}
            />
          </div>
        </div>
        <div className={styles.cardSoft} style={{ marginTop: 26 }}>
          <div className={styles.valueNote} style={{ marginTop: 0, color: 'var(--text-body)' }}>
            Die Abschreibung beträgt {eur(tax.depreciationCents)} im Jahr und {tax.depreciationOrigin}. Weil
            der anerkannte Aufwand die Mieteinnahmen übersteigt, entsteht ein Verlust von {eur(tax.lossCents)}
            , den Sie gegen Ihr Gehalt rechnen.
          </div>
          <div className={styles.rowBetween} style={{ marginTop: 18, justifyContent: 'flex-start', gap: 14 }}>
            <span className={styles.valueNote} style={{ marginTop: 0 }}>
              {eur(tax.lossCents)} × {pct(values.taxRatePct, 0)} =
            </span>
            <span className={styles.resultBoxValue} style={{ marginTop: 0 }}>
              {eur(tax.refundYearCents)}
            </span>
            <span className={styles.valueNote} style={{ marginTop: 0 }}>
              im Jahr · {eur(tax.refundMonthCents)} im Monat
            </span>
          </div>
        </div>
      </Question>

      <Question
        number="07"
        title="Wann bekomme ich den Steuervorteil?"
        text="Nicht erst bei der Steuererklärung. Der Verlust wird als Freibetrag eingetragen und wirkt ab dem Folgemonat in der Abrechnung."
        aside={
          <div className={styles.questionNote}>
            {tax.incomeSource === 'contact'
              ? `Beträge illustrativ, auf Grundlage des Einkommens aus der Selbstauskunft und Ihres persönlichen Steuersatzes von ${pct(values.taxRatePct, 0)}.`
              : `Beträge illustrativ, auf Grundlage eines angenommenen Einkommens von ${eur(tax.grossMonthlyCents * 12)} im Jahr und Ihres persönlichen Steuersatzes von ${pct(values.taxRatePct, 0)}.`}
          </div>
        }
      >
        <div className={styles.twoCols}>
          <div className={styles.compareCard}>
            <div className={styles.compareHead}>
              <div className={styles.eyebrow11}>Heute</div>
              <div className={styles.compareHeadTitle}>Ohne Eintrag</div>
            </div>
            <div className={styles.compareBody}>
              <div className={styles.compareRow}>
                <span className={styles.factLabel}>Zu versteuerndes Einkommen, monatlich</span>
                <span className={styles.factValue}>{eur(tax.grossMonthlyCents)}</span>
              </div>
              <div className={styles.compareRow}>
                <span className={styles.factLabel}>Freibetrag aus Vermietung</span>
                <span className={styles.factValue} style={{ color: 'var(--text-subtle)' }}>
                  0 €
                </span>
              </div>
              <div className={styles.compareRow}>
                <span className={styles.factLabel}>Lohnsteuer</span>
                <span className={styles.factValue}>−{eur(tax.wageTaxOldCents)}</span>
              </div>
              <div className={styles.compareTotal}>
                <span className={styles.positionTitle} style={{ fontSize: 14 }}>
                  Nach Lohnsteuer
                </span>
                <span className={styles.compareTotalValue}>{eur(tax.netOldCents)}</span>
              </div>
            </div>
          </div>
          <div className={styles.compareCard}>
            <div className={clsx(styles.compareHead, styles.compareHeadAccent)}>
              <div className={clsx(styles.eyebrow11, styles.miniHeadAccent)}>Nach dem Antrag</div>
              <div className={styles.compareHeadTitle}>Freibetrag eingetragen</div>
            </div>
            <div className={styles.compareBody}>
              <div className={styles.compareRow}>
                <span className={styles.factLabel}>Zu versteuerndes Einkommen, monatlich</span>
                <span className={styles.factValue}>{eur(tax.grossMonthlyCents)}</span>
              </div>
              <div className={styles.compareRow}>
                <span className={styles.factLabel} style={{ color: 'var(--text-accent)' }}>
                  Freibetrag aus Vermietung
                </span>
                <span className={styles.factValue} style={{ color: 'var(--text-accent)' }}>
                  {eur(tax.allowanceMonthlyCents)}
                </span>
              </div>
              <div className={styles.compareRow}>
                <span className={styles.factLabel}>Lohnsteuer</span>
                <span className={styles.factValue}>−{eur(tax.wageTaxNewCents)}</span>
              </div>
              <div className={styles.compareTotal}>
                <span className={styles.positionTitle} style={{ fontSize: 14 }}>
                  Nach Lohnsteuer
                </span>
                <span className={styles.compareTotalValue}>{eur(tax.netNewCents)}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.resultBox}>
          <div style={{ flex: 1 }}>
            <div className={clsx(styles.eyebrow11, styles.miniHeadAccent)}>
              Mehr auf dem Konto, jeden Monat
            </div>
            <div className={styles.resultBoxValue}>+{eur(tax.refundMonthCents)}</div>
          </div>
          <div className={styles.valueNoteSmall} style={{ flex: 2, marginTop: 0, fontSize: 13.5 }}>
            Der Freibetrag von {eur(tax.allowanceMonthlyCents)} im Monat ist der steuerliche Verlust der
            Wohnung — {eur(tax.lossCents)} im Jahr. Bei {pct(values.taxRatePct, 0)} Grenzsteuersatz sind das{' '}
            {eur(tax.refundYearCents)} weniger Lohnsteuer. Beantragt wird er beim Finanzamt als
            Lohnsteuerermäßigung.
          </div>
        </div>
      </Question>

      <Question
        number="08"
        title="Wer verwaltet die Wohnung für mich?"
        text="Sie selbst haben mit dem Haus nichts zu tun. Die Gemeinschaft beauftragt einen WEG-Verwalter, um Ihre Wohnung kümmert sich die Sondereigentumsverwaltung."
        aside={
          <div className={styles.questionNote}>Monatswerte für {runningCosts.areaLabel}, erstes Jahr.</div>
        }
      >
        <div className={styles.roleCards}>
          <div className={styles.roleCard}>
            <div className={styles.eyebrow11}>Gemeinsames Eigentum</div>
            <div className={styles.roleTitle}>WEG-Verwalter</div>
            <div className={styles.roleText}>
              Dach, Fassade, Treppenhaus, Heizung, Versicherung, Jahresabrechnung, Eigentümerversammlung.
            </div>
          </div>
          <div className={styles.roleCard}>
            <div className={styles.eyebrow11}>Ihre Wohnung</div>
            <div className={styles.roleTitle}>Sondereigentumsverwaltung</div>
            <div className={styles.roleText}>
              Mietvertrag, Nebenkostenabrechnung, Mieterwechsel, Handwerker in der Wohnung.
            </div>
          </div>
          <div className={styles.roleCard}>
            <div className={styles.eyebrow11}>Innerhalb der Wohnung</div>
            <div className={styles.roleTitle}>Der Mieter</div>
            <div className={styles.roleText}>
              Verbrauch, Kleinreparaturen im Rahmen des Mietvertrags, laufende Betriebskosten.
            </div>
          </div>
        </div>
        <div className={styles.compareCard} style={{ marginTop: 26 }}>
          <div
            className={clsx(styles.compareHead, styles.compareHeadAccent)}
            style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <div style={{ flex: 1 }}>
              <div className={clsx(styles.eyebrow11, styles.miniHeadAccent)}>Nicht umlagefähig</div>
              <div className={styles.compareHeadTitle}>Laufende Kosten in Ihrer Rechnung</div>
            </div>
            <span className={styles.compareTotalValue} style={{ fontSize: 20 }}>
              {eur(runningCosts.totalCents)}
            </span>
          </div>
          <div className={styles.compareBody} style={{ padding: '4px 20px 16px' }}>
            {runningCosts.rows.map((row) => (
              <div key={row.label} className={styles.positionRow} style={{ padding: '13px 0' }}>
                <div>
                  <div className={styles.positionTitle}>{row.label}</div>
                  <div className={styles.positionNote} style={{ marginTop: 5 }}>
                    {row.note} {row.deductible ? 'Steuerlich absetzbar.' : 'Steuerlich nicht absetzbar.'}
                  </div>
                </div>
                <span className={styles.positionValue}>{eur(row.cents)}</span>
              </div>
            ))}
            <div className={styles.valueNoteSmall} style={{ marginTop: 14, fontSize: 12.5 }}>
              Zusammen {eur(runningCosts.totalCents)} im Monat ({eur2(runningCosts.perSqmCents)} je m²), davon{' '}
              {eur(runningCosts.deductibleCents)} als Werbungskosten absetzbar.
              {runningCosts.serviceChargeCents === null
                ? ''
                : ` Das Hausgeld der Einheit beträgt ${eur(runningCosts.serviceChargeCents)} im Monat; der umlagefähige Teil läuft über die Nebenkosten des Mieters.`}
            </div>
          </div>
        </div>
      </Question>

      <Question
        number="09"
        title="Was passiert, wenn etwas kaputtgeht?"
        text="Für Dach, Fassade, Heizung und Aufzug spart die Gemeinschaft über die Instandhaltungsrücklage an. Ihr Anteil ist in der Rechnung bereits enthalten."
      >
        <div className={styles.twoCols} style={{ gap: 22 }}>
          <div className={styles.cardSoft}>
            <div className={styles.eyebrow11}>Instandhaltungsrücklage</div>
            <div className={styles.compareHeadTitle} style={{ marginTop: 8 }}>
              Rücklage der Gemeinschaft
            </div>
            <div
              className={styles.rowBetween}
              style={{ marginTop: 12, justifyContent: 'flex-start', gap: 26 }}
            >
              <div>
                <div className={styles.eyebrow11}>Ihr Anteil im Monat</div>
                <div className={styles.compareTotalValue} style={{ marginTop: 4 }}>
                  {eur(reserve.monthlyCents)}
                </div>
              </div>
              <div>
                <div className={styles.eyebrow11}>Im Jahr</div>
                <div className={styles.compareTotalValue} style={{ marginTop: 4 }}>
                  {eur(reserve.yearlyCents)}
                </div>
              </div>
            </div>
            <div className={styles.valueNoteSmall} style={{ marginTop: 12, color: 'var(--text-body)' }}>
              Zufluss aus Ihrem Hausgeld nach § 19 WEG
              {reserve.perSqmYearCents === null ? '' : `, ${eur2(reserve.perSqmYearCents)} je m² im Jahr`}.
              Die Rücklage liegt auf dem Konto der Gemeinschaft und ist steuerlich erst absetzbar, wenn sie
              verwendet wird.
            </div>
          </div>
          <div className={styles.cardAccentSoft}>
            <div className={clsx(styles.eyebrow11, styles.miniHeadAccent)}>In zehn Jahren</div>
            <div className={styles.compareHeadTitle} style={{ marginTop: 8 }}>
              Angespart über Ihr Hausgeld
            </div>
            <div className={styles.resultBoxValue}>{eur(reserve.tenYearsCents)}</div>
            <div className={styles.valueNoteSmall} style={{ marginTop: 12, color: 'var(--text-body)' }}>
              Größere Maßnahmen am Gemeinschaftseigentum beschließt die Eigentümerversammlung und bezahlt sie
              aus der Rücklage. Reicht sie nicht, folgt eine Sonderumlage nach Miteigentumsanteil.
            </div>
          </div>
        </div>
      </Question>

      <section className={styles.summary} aria-label="Zusammenfassung">
        <div className={styles.summaryEyebrow}>Zusammenfassung · Stand Jahr {year}</div>
        <div className={styles.summaryGrid}>
          <div>
            <div className={styles.summaryLead}>
              Aus {eur(result.equityCents)} Eigenkapital und durchschnittlich{' '}
              {eur(saleInfo.averageOwnPaymentMonthlyCents)} pro Monat Eigenanteil werden
            </div>
            <div className={styles.summaryValue}>{eur(sale.equityCents)}</div>
            <div className={styles.summaryText}>
              Gesamtgewinn bei Verkauf in Jahr {year} · Netto-Zuwachs {sgn(sale.netGainCents)} € ·{' '}
              {pct(result.irrPct)} pro Jahr
            </div>
            <div className={styles.summaryMetrics}>
              <div>
                <div className={styles.summaryMetricLabel}>Einmal beim Kauf</div>
                <div className={styles.summaryMetricValue}>{eur(result.equityCents)}</div>
              </div>
              <div>
                <div className={styles.summaryMetricLabel}>Jeden Monat</div>
                <div className={styles.summaryMetricValue}>{sgn(result.monthly.cashflowAfterTaxCents)} €</div>
              </div>
              <div>
                <div className={styles.summaryMetricLabel}>Davon trägt der Mieter</div>
                <div className={styles.summaryMetricValue}>
                  {percentOfSplit(installmentRow?.installment.tenantCents ?? 0)}
                </div>
              </div>
            </div>
          </div>
          <div>
            <YearBars bars={view.bars} onSelect={onYearChange} ink />
            <Legend className={styles.inkLegend} items={legendInk} />
            <div className={styles.inkYearRow}>
              <div className={styles.rowBetween} style={{ justifyContent: 'flex-start', gap: 10 }}>
                <span className={styles.summaryMetricLabel}>Zeitpunkt</span>
                <span className={styles.inkYearValue}>Jahr {year}</span>
              </div>
              <input
                type="range"
                className={clsx(styles.range, styles.rangeInk)}
                min={1}
                max={30}
                step={1}
                value={year}
                aria-label="Verkaufsjahr"
                aria-valuetext={`Jahr ${year}`}
                onChange={(event) => onYearChange(Number(event.target.value))}
              />
              <div className={styles.inkSentence}>{view.breakEvenSentence}</div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
